import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useCreateEstimate, useCreateCustomer, useListCustomers, useListEstimates, getListCustomersQueryKey, useAttachQuote, getProjectAggregatesQueryKey, getProjectQuotesQueryKey, getProjectDocumentLinksQueryKey } from "@workspace/api-client-react";
import { usePageTitle } from "@/hooks/use-page-title";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Loader2, Check, Hash, PlusCircle } from "lucide-react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { CreateCustomerModal, type CreateCustomerFormValues } from "@/components/create-customer-modal";
import { EditorDisclaimer } from "@/components/editor-disclaimer";
import { trackEvent } from "@/lib/analytics";
import { emitTourEvent, useGuidedTour } from "@/hooks/use-guided-tour";

const estimateSchema = z.object({
  customerId: z.number({ required_error: "Please select a customer" }).min(1, "Please select a customer"),
  estimateNumber: z.string().optional(),
  estimateDate: z.string().min(1, "Date is required"),
  validUntil: z.string().optional(),
  description: z.string().optional(),
});

function nextEstimateNumberPreview(lastNumber: string | null | undefined): string {
  if (!lastNumber) return "1";
  const lastSegment = lastNumber.split("-").pop() ?? lastNumber;
  const num = parseInt(lastSegment, 10);
  if (isNaN(num)) return "1";
  const prefix = lastNumber.includes("-") ? lastNumber.slice(0, lastNumber.lastIndexOf("-") + 1) : "";
  return `${prefix}${num + 1}`;
}

export default function NewEstimate() {
  usePageTitle("New Quote");
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const queryParams = new URLSearchParams(window.location.search);
  const initialCustomerId = queryParams.get("customer") ? parseInt(queryParams.get("customer")!, 10) : undefined;
  const initialProjectId = queryParams.get("project") ? parseInt(queryParams.get("project")!, 10) : undefined;
  const attachQuote = useAttachQuote();

  const queryClient = useQueryClient();
  const { data: customers, isLoading: isLoadingCustomers } = useListCustomers();
  const { data: allEstimates } = useListEstimates({});
  const createEstimate = useCreateEstimate();
  const createCustomer = useCreateCustomer();

  const [customerSearch, setCustomerSearch] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [pendingCustomerName, setPendingCustomerName] = useState("");

  const today = new Date().toISOString().slice(0, 10);
  const thirtyDaysOut = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

  const lastEstimateNumber = allEstimates && allEstimates.length > 0
    ? allEstimates.reduce((latestEst, est) => {
        if (!latestEst) return est;
        const latestDate = new Date(latestEst.createdAt).getTime();
        const estDate = new Date(est.createdAt).getTime();
        return estDate > latestDate ? est : latestEst;
      }, null as typeof allEstimates[0] | null)?.estimateNumber ?? null
    : null;

  const nextNumber = nextEstimateNumberPreview(lastEstimateNumber);

  const form = useForm<z.infer<typeof estimateSchema>>({
    resolver: zodResolver(estimateSchema),
    defaultValues: {
      estimateNumber: "",
      estimateDate: today,
      validUntil: thirtyDaysOut,
      description: "",
      customerId: initialCustomerId || undefined,
    },
  });

  useEffect(() => {
    if (allEstimates !== undefined) {
      const current = form.getValues("estimateNumber");
      if (!current) {
        form.setValue("estimateNumber", nextNumber);
      }
    }
  }, [allEstimates]);

  // During the guided tour, pre-select the seeded demo customer so the user
  // can focus on building the quote rather than hunting for a customer.
  const { active: tourActive, refs: tourRefs } = useGuidedTour();
  useEffect(() => {
    if (!tourActive) return;
    const demoCustomerId = tourRefs.customerIds[0];
    if (demoCustomerId && !form.getValues("customerId")) {
      form.setValue("customerId", demoCustomerId);
    }
  }, [tourActive, tourRefs.customerIds, form]);

  const onSubmit = (data: z.infer<typeof estimateSchema>) => {
    createEstimate.mutate({
      data: {
        estimateNumber: data.estimateNumber || undefined,
        customerId: data.customerId,
        estimateDate: data.estimateDate,
        validUntil: data.validUntil || null,
        description: data.description,
      }
    }, {
      onSuccess: (estimate) => {
        toast({ title: "Quote created successfully" });
        emitTourEvent("quote.created", { quoteId: estimate.id });
        // Server-backed per-user dedup via userEventsTable; falls back silently on error
        fetch("/api/user/activate", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" } })
          .then(r => r.json())
          .then(({ firstTime }: { firstTime: boolean }) => { if (firstTime) trackEvent("Activated"); })
          .catch(() => {});
        if (initialProjectId) {
          attachQuote.mutate(
            { projectId: initialProjectId, quoteId: estimate.id },
            {
              onSettled: () => {
                queryClient.invalidateQueries({ queryKey: getProjectQuotesQueryKey(initialProjectId) });
                queryClient.invalidateQueries({ queryKey: getProjectAggregatesQueryKey(initialProjectId) });
                queryClient.invalidateQueries({ queryKey: getProjectDocumentLinksQueryKey() });
                setLocation(`/quotes/${estimate.id}`);
              },
            },
          );
          return;
        }
        setLocation(`/quotes/${estimate.id}`);
      },
      onError: () => {
        toast({ title: "Failed to create quote", variant: "destructive" });
      }
    });
  };

  const handleCreateCustomer = (data: CreateCustomerFormValues) => {
    createCustomer.mutate(
      {
        data: {
          name: data.name,
          email: data.email || undefined,
          phone: data.phone || undefined,
          addressLine1: data.addressLine1 || undefined,
        },
      },
      {
        onSuccess: (newCustomer) => {
          queryClient.invalidateQueries({ queryKey: getListCustomersQueryKey() });
          form.setValue("customerId", newCustomer.id);
          setCustomerSearch("");
          setCreateModalOpen(false);
        },
        onError: () => {
          toast({ title: "Failed to create customer", variant: "destructive" });
        },
      }
    );
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-in fade-in duration-300">
      <Link href="/quotes" className="text-sm font-medium text-muted-foreground hover:text-foreground flex items-center gap-1 w-fit transition-colors">
        <ArrowLeft className="w-4 h-4" />
        Back to Quotes
      </Link>

      <div>
        <h1 className="text-3xl font-bold tracking-tight">Create New Quote</h1>
        <p className="text-muted-foreground mt-1">Start a new project quote for a customer.</p>
      </div>

      <Card className="shadow-sm border-border/50" data-tour-target="quote-form">
        <CardContent className="pt-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">

              <FormField
                control={form.control}
                name="customerId"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Customer</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            role="combobox"
                            className={cn(
                              "w-full justify-between font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                            disabled={isLoadingCustomers}
                          >
                            {field.value
                              ? customers?.find((c) => c.id === field.value)?.name
                              : "Select customer..."}
                            <span className="opacity-50 shrink-0">▼</span>
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-[400px] p-0" align="start">
                        <Command>
                          <CommandInput
                            placeholder="Search customer..."
                            value={customerSearch}
                            onValueChange={setCustomerSearch}
                          />
                          <CommandList>
                            {(() => {
                              const trimmed = customerSearch.trim();
                              const hasExactMatch = customers?.some(
                                (c) => c.name.toLowerCase() === trimmed.toLowerCase()
                              );
                              const showCreate = trimmed.length > 0 && !hasExactMatch;
                              const hasCustomers = customers && customers.length > 0;

                              return (
                                <>
                                  {!hasCustomers && !showCreate && (
                                    <CommandEmpty>No customer found.</CommandEmpty>
                                  )}
                                  {hasCustomers && (
                                    <CommandGroup>
                                      {customers?.map((customer) => (
                                        <CommandItem
                                          key={customer.id}
                                          value={customer.name}
                                          onSelect={() => {
                                            form.setValue("customerId", customer.id);
                                            setCustomerSearch("");
                                            document.body.click();
                                          }}
                                        >
                                          <Check
                                            className={cn(
                                              "mr-2 h-4 w-4",
                                              customer.id === field.value ? "opacity-100" : "opacity-0"
                                            )}
                                          />
                                          {customer.name}
                                        </CommandItem>
                                      ))}
                                    </CommandGroup>
                                  )}
                                  {showCreate && (
                                    <CommandGroup>
                                      <CommandItem
                                        value={`__create__${trimmed}`}
                                        onSelect={() => {
                                          setPendingCustomerName(trimmed);
                                          setCreateModalOpen(true);
                                          document.body.click();
                                        }}
                                      >
                                        <PlusCircle className="mr-2 h-4 w-4 text-muted-foreground" />
                                        Create &quot;{trimmed}&quot;
                                      </CommandItem>
                                    </CommandGroup>
                                  )}
                                </>
                              );
                            })()}
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="estimateNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-1.5">
                        <Hash className="w-3.5 h-3.5 text-muted-foreground" />
                        Quote #
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          className="font-mono"
                          placeholder={nextNumber}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="estimateDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Date</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="validUntil"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Valid Until</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Scope / Description (Optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Brief overview of the project scope..."
                        className="min-h-[100px] resize-y"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-end gap-4 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setLocation("/quotes")}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createEstimate.isPending}>
                  {createEstimate.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Create Quote
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>

      <CreateCustomerModal
        open={createModalOpen}
        initialName={pendingCustomerName}
        isSubmitting={createCustomer.isPending}
        onOpenChange={setCreateModalOpen}
        onSubmit={handleCreateCustomer}
      />

      <EditorDisclaimer />
    </div>
  );
}
